import { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Nav, Row } from 'react-bootstrap';
import i18next from 'i18next';
import { ExerciseDto } from '../../../../../../shared/dtos/exercise.dto';
import { ExerciseTagDto } from '../../../../../../shared/dtos/exerciseTag.dto';
import { PagedDto } from '../../../../../../shared/dtos/paged.dto';
import { ScopeKey } from '../../../../util/types';
import AppModal from '../../../../components/app-modal/AppModal';
import PrimaryButton from '../../../../components/PrimaryButton';
import SearchBar from '../../../../components/SearchBar';
import { useExerciseFilters } from '../../../../hooks/useExerciseFilters';
import { MIN_SEARCH_LENGTH, useExerciseSearch } from '../../../../hooks/useExerciseSearch';
import CustomExerciseActions, { CustomExercise } from './CustomExerciseActions';
import ExercisePickerChips from './ExercisePickerChips';
import ExercisePickerResults from './ExercisePickerResults';
import ExercisePickerSelectionBar from './ExercisePickerSelectionBar';
import ExercisePickerSidebar from './ExercisePickerSidebar';

export type { CustomExercise };

type ExercisePickerModalProps = {
  show: boolean;
  onCreateExercise: (exercises: ExerciseDto[]) => void;
  onCreateCustomExercise: (exercise: CustomExercise) => void;
  onNegative: () => void;
};

const SEARCH_DEBOUNCE_MS: number = 400;
const SCOPES: ScopeKey[] = ['tenant', 'core'];
const PANE_HEIGHT: string = '45vh';
const NAV_HEIGHT: string = '3.7rem';
const NAV_BLOCK_HEIGHT: string = '4.7rem';
const RESULTS_HEIGHT: string = `calc(${PANE_HEIGHT} - ${NAV_BLOCK_HEIGHT})`;

function flattenPages(pages?: PagedDto<ExerciseDto>[]): ExerciseDto[] {
  if (!pages) {
    return [];
  }

  const seen: Set<string> = new Set();
  const flattened: ExerciseDto[] = [];

  for (const page of pages) {
    for (const exercise of page.items) {
      if (seen.has(exercise.id)) {
        continue;
      }

      seen.add(exercise.id);
      flattened.push(exercise);
    }
  }

  return flattened;
}

const ExercisePickerModal = ({
  show,
  onCreateExercise,
  onCreateCustomExercise,
  onNegative,
}: ExercisePickerModalProps): ReactElement => {
  const [activeScope, setActiveScope] = useState<ScopeKey>('core');
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [selectedByCategory, setSelectedByCategory] = useState<Record<string, string[]>>({});
  const [searchKey, setSearchKey] = useState('');
  const [searchResetToken, setSearchResetToken] = useState(0);
  const [selectedExercises, setSelectedExercises] = useState<ExerciseDto[]>([]);

  const { data: filters } = useExerciseFilters(show);

  const tenantQuery = useExerciseSearch({ scope: 'tenant', searchKey, selectedByCategory, enabled: show });
  const coreQuery = useExerciseSearch({ scope: 'core', searchKey, selectedByCategory, enabled: show });

  const activeQuery = activeScope === 'tenant' ? tenantQuery : coreQuery;

  const tenantExercises: ExerciseDto[] = useMemo(() => flattenPages(tenantQuery.data?.pages), [tenantQuery.data]);
  const coreExercises: ExerciseDto[] = useMemo(() => flattenPages(coreQuery.data?.pages), [coreQuery.data]);

  const activeExercises: ExerciseDto[] = activeScope === 'tenant' ? tenantExercises : coreExercises;
  const activeTotalCount: number = activeQuery.data?.pages[0]?.totalCount ?? 0;

  const selectedIds: Set<string> = useMemo(
    () => new Set(selectedExercises.map((exercise: ExerciseDto) => exercise.id)),
    [selectedExercises]
  );

  const hasActiveFilters: boolean = useMemo(
    () => searchKey.length > 0 || Object.keys(selectedByCategory).length > 0,
    [searchKey, selectedByCategory]
  );

  const showsSearchHint: boolean = searchKey.length > 0 && searchKey.length < MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!show) {
      return;
    }

    setActiveScope('core');
    setOpenCategoryId(null);
    setSelectedByCategory({});
    setSearchKey('');
    setSelectedExercises([]);
    setSearchResetToken((previous: number) => previous + 1);
  }, [show]);

  const onSearch = useCallback((value: string) => {
    setSearchKey(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchKey('');
    setSearchResetToken((previous: number) => previous + 1);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const resetAll = useCallback(() => {
    setSelectedByCategory({});
    setSearchKey('');
    setSearchResetToken((previous: number) => previous + 1);
  }, []);

  const onToggleCategory = useCallback((categoryId: string) => {
    setOpenCategoryId((previous: string | null) => (previous === categoryId ? null : categoryId));
  }, []);

  const onToggleTag = useCallback((tag: ExerciseTagDto, checked: boolean) => {
    setSelectedByCategory((previous: Record<string, string[]>) => {
      const current: string[] = previous[tag.categoryId] ?? [];
      const next: string[] = checked
        ? Array.from(new Set([...current, tag.id]))
        : current.filter((id: string) => id !== tag.id);

      const updated: Record<string, string[]> = { ...previous };

      if (next.length > 0) {
        updated[tag.categoryId] = next;
      } else {
        delete updated[tag.categoryId];
      }

      return updated;
    });
  }, []);

  const onRemoveTag = useCallback(
    (tag: ExerciseTagDto) => {
      onToggleTag(tag, false);
    },
    [onToggleTag]
  );

  const onToggleSelection = useCallback((exercise: ExerciseDto) => {
    setSelectedExercises((previous: ExerciseDto[]) => {
      if (previous.some((selected: ExerciseDto) => selected.id === exercise.id)) {
        return previous.filter((selected: ExerciseDto) => selected.id !== exercise.id);
      }

      return [...previous, exercise];
    });
  }, []);

  const onConfirmSelection = (): void => {
    onCreateExercise(selectedExercises);
    setSelectedExercises([]);
  };

  const buildTabTitle = (scope: ScopeKey): string => {
    const name: string = i18next.t(scope === 'tenant' ? 'exerciseModal.tenantExercises' : 'exerciseModal.coreExercises');
    const query = scope === 'tenant' ? tenantQuery : coreQuery;
    const count: number | undefined = query.data?.pages[0]?.totalCount;

    if (count === undefined) {
      return name;
    }

    return i18next.t('exerciseModal.tabTitle', { name, count });
  };

  const renderResults = (scope: ScopeKey): ReactElement => (
    <ExercisePickerResults
      scope={scope}
      exercises={activeExercises}
      totalCount={activeTotalCount}
      selectedIds={selectedIds}
      isLoading={activeQuery.isPending}
      isError={activeQuery.isError}
      isFetchingNextPage={activeQuery.isFetchingNextPage}
      hasNextPage={activeQuery.hasNextPage}
      hasActiveFilters={hasActiveFilters}
      onToggleSelection={onToggleSelection}
      onLoadMore={() => void activeQuery.fetchNextPage()}
      onRetry={() => void activeQuery.refetch()}
      onResetFilters={resetAll}
    />
  );

  return (
    <AppModal size="xl" dialogClassName="modal-90w" show={show} onHide={onNegative}>
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('exerciseModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="overflow-hidden">
        <div className="mb-3">
          <SearchBar
            key={`exercise-search-${searchResetToken}`}
            onSearch={onSearch}
            debounceMs={SEARCH_DEBOUNCE_MS}
            maxWidth={undefined}
          />

          {showsSearchHint && (
            <div className="text-muted small mt-1">
              <i className="fas fa-info-circle me-1" />
              {i18next.t('exerciseModal.searchInfo')}
            </div>
          )}
        </div>

        <div>
          <ExercisePickerChips
            filters={filters}
            selectedByCategory={selectedByCategory}
            searchKey={searchKey}
            onRemoveTag={onRemoveTag}
            onClearSearch={clearSearch}
            onResetAll={resetAll}
          />
        </div>

        <Row>
          <Col
            xl={3}
            md={4}
            xs={12}
            className="border-end overflow-auto"
            style={{ height: PANE_HEIGHT }}
          >
            <ExercisePickerSidebar
              filters={filters}
              openCategoryId={openCategoryId}
              selectedByCategory={selectedByCategory}
              hasActiveFilters={Object.keys(selectedByCategory).length > 0}
              onToggleCategory={onToggleCategory}
              onToggleTag={onToggleTag}
              onResetFilters={resetFilters}
            />
          </Col>

          <Col xl={9} md={8} xs={12}>
            <Nav variant="tabs" className="mb-3 flex-nowrap" style={{ height: NAV_HEIGHT }}>
              {SCOPES.map((scope: ScopeKey) => (
                <Nav.Item key={`scope-${scope}`}>
                  <Nav.Link
                    active={activeScope === scope}
                    onClick={() => setActiveScope(scope)}
                    className="border-0 border-bottom border-3 rounded-0 px-3 py-2 text-nowrap fw-semibold"
                    style={{
                      color: activeScope === scope ? 'var(--bs-primary)' : '#6c757d',
                      borderColor: activeScope === scope ? 'var(--bs-primary)' : 'transparent',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {buildTabTitle(scope)}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <div className="overflow-auto" style={{ height: RESULTS_HEIGHT }}>
              {renderResults(activeScope)}
            </div>
          </Col>
        </Row>

        <div>
          <ExercisePickerSelectionBar selectedExercises={selectedExercises} onRemove={onToggleSelection} />
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
        <CustomExerciseActions onCreateCustomExercise={onCreateCustomExercise} />

        <PrimaryButton disabled={selectedExercises.length === 0} type="button" onClick={onConfirmSelection}>
          {i18next.t('exerciseModal.selectionDone', { count: selectedExercises.length })}
        </PrimaryButton>
      </Modal.Footer>
    </AppModal>
  );
};

export default memo(ExercisePickerModal);
